import { Component, ojs, app, payload, state, v } from "modular-openscriptjs";
import {
	pageInfo,
	either,
	inform,
	scrollToTop,
	cleanDispose,
	processStart,
	processEnd,
} from "../../../helpers";
import { dom, tool } from "../../../fotastart";
import { PAGES, STRINGS, api } from "../../../declarations";
import FormField from "../../Blocks/FormField";

const h = app("h");
const gc = app("gc");
const broker = app("broker");
const $e = app("$e");
const route = app("router");

export default class EditProfile extends Component {
	usernameAvailable = state(true);
	debounceTimer = null;
	deleteAccountModal = null;
	deleteAccountOtpInput = null;

	render(user, ...args) {
		if (route.is("user.edit-profile")) {
			pageInfo(PAGES.EDIT_PROFILE);
			scrollToTop();
		}

		return h.div(
			{
				class: "min-vh-100 py-4 pb-5 mb-5 mx-auto w-100 py-md-5",
				style: "max-width: 900px;"
			},
			h.div(
				{ class: "container" },

				// Header
				h.div(
					{ class: "mb-4 d-flex align-items-center justify-content-between" },
					h.div(
						{ class: "d-flex align-items-center gap-3" },
						h.button(
							{
								class: "btn btn-white border shadow-sm circle p-2",
								onclick: "history.back()",
							},
							h.i({ class: "bi bi-arrow-left fs-5 text-secondary" })
						),
						h.h2({ class: "fw-bold mb-0 h4" }, "Edit Profile")
					),
				),

				h.input({
					type: "hidden",
					name: "id",
					value: user.value?.id,
				}),

				// Responsive Flex Wrapper
				h.div(
					{ class: "d-flex flex-wrap gap-4 mb-4" },

					// Card 1: Identity & Username
					h.div(
						{
							class: "flex-fill",
							style: "background: #fff; border-radius: 16px; border: 1px solid rgba(0,0,0,0.08); box-shadow: 0 4px 24px rgba(0,0,0,0.03); padding: 24px; min-width: 320px; flex: 1 1 calc(50% - 12px);"
						},
						h.h5({ class: "fw-bold mb-3 pb-3 text-dark d-flex align-items-center gap-2" },
							h.i({ class: "bi bi-person-badge text-primary" }),
							"Identity Details"
						),
						h.div(
							{ class: "d-grid gap-3" },

							// First Name
							h.FormField({
								name: "first-name",
								id: "profile-first-name",
								value: user.value?.first_name,
								label: { value: "First Name" }
							}),

							// Last Name
							h.FormField({
								name: "last-name",
								id: "profile-last-name",
								value: user.value?.last_name,
								label: { value: "Last Name" }
							}),

							// Display Name
							h.FormField({
								name: "display-name",
								id: "profile-display-name",
								value: user.value?.display_name,
								label: { value: "Display Name" },
								helpText: { value: "This name will be visible to others" }
							}),

							// Username
							h.FormField({
								name: "username",
								id: "profile-username",
								value: user.value?.username,
								label: { value: "Username" },
								helpText: { value: "Your unique @username" },
								icons: {
									leading: "@",
									trailing: v(this.usernameAvailable, (available) => {
										if (available.value === true) {
											return h.i({ class: "bi bi-check-circle-fill text-success fs-5" });
										}
										if (available.value === false) {
											return h.i({ class: "bi bi-x-circle-fill text-danger fs-5" });
										}
										if (available.value === "processing") {
											return h.div({ class: "spinner-border spinner-border-sm text-primary", role: "status" });
										}
										return null;
									})
								},
								oninput: this.method("checkUsernameAvailability", [
									"${this}",
								]),
								listeners: {
									input: function () {
										this.value = this.value
											.toLowerCase()
											.replace(/[^a-z0-9._-]/g, "");
									},
								},
							})
						)
					),

					// Card 2: Contact & Demographics
					h.div(
						{
							class: "flex-fill",
							style: "background: #fff; border-radius: 16px; border: 1px solid rgba(0,0,0,0.08); box-shadow: 0 4px 24px rgba(0,0,0,0.03); padding: 24px; min-width: 320px; flex: 1 1 calc(50% - 12px);"
						},
						h.h5({ class: "fw-bold mb-3 pb-3 text-dark d-flex align-items-center gap-2" },
							h.i({ class: "bi bi-telephone text-success" }),
							"Contact & About"
						),
						h.div(
							{ class: "d-grid gap-3" },

							// Phone Number
							h.div(
								{ class: "form-field-group" },
								h.FormField(
									{
										tag: "select",
										id: "profile-country-code",
										label: { value: "Code" },
										style: "max-width: 96px;"
									},
									h.option({ value: "+254" }, "+254")
								),
								h.FormField(
									{
										type: "tel",
										name: "phone_number",
										id: "profile-phone",
										value: user.value?.phone?.substring((user.value?.phone?.length ?? 9) - 9) ?? "",
										placeholder: "7xxxxx...",
										label: { value: "Phone Number" }
									},
									{
										c_attr: { class: "flex-fill" }
									}
								)
							),

							// Bio
							h.FormField({
								tag: "textarea",
								id: "profile-bio",
								rows: 2,
								placeholder: "Short Bio (optional)",
								value: user?.value?.bio,
								label: { value: "Short Bio (optional)" },
								helpText: {
									value: h.span(
										"Add a bio (",
										h.span({ id: "bio-characters-count" }, 255 - (user?.value?.bio?.length ?? 0)),
										" characters left)"
									)
								},
								listeners: {
									input: function () {
										let elem = dom.id("profile-bio");
										if (elem.value.length > 255) {
											elem.value = elem.value.substring(0, 255);
										}
										dom.id("bio-characters-count").innerHTML = 255 - elem.value.length;
									},
								},
							}),

							// Gender
							h.FormField(
								{
									tag: "select",
									name: "profile_gender",
									id: "profile-gender",
									label: { value: "Gender" },
									value: user.value?.gender
								},
								h.option({ value: "" }, "Select Gender"),
								h.option({ value: "male" }, "Male", either(user.value?.gender === "male", { selected: true })),
								h.option({ value: "female" }, "Female", either(user.value?.gender === "female", { selected: true })),
								h.option({ value: "none" }, "Prefer Not To Say", either(user.value?.gender === "none", { selected: true }))
							),

							// Date Of Birth
							h.FormField({
								type: "date",
								name: "profile_dob",
								id: "profile-dob",
								value: user.value?.dob,
								label: { value: "Date Of Birth" },
								max: (() => {
									const maxDate = new Date();
									maxDate.setFullYear(maxDate.getFullYear() - 16);
									return maxDate.toISOString().split("T")[0];
								})()
							})
						)
					)
				),

				// Save button card
				h.div(
					{ class: "d-flex justify-content-end" },
					h.button(
						{
							type: "button",
							class: "btn btn-primary rounded-pill px-4 py-2.5 fw-bold shadow-sm d-flex align-items-center gap-2",
							onclick: this.method("updateProfile"),
						},
						h.span({}, "Update Profile"),
						h.i({ class: "bi bi-check2 fs-5" })
					)
				),

				// Danger zone: Delete Account. Kept at the bottom of the page
				// (not tucked in a menu) so it's easy to find - App Store
				// Review Guideline 5.1.1(v) requires in-app account deletion
				// to be clearly discoverable.
				h.div(
					{
						class: "mt-5 border border-danger-subtle rounded-4 p-4",
						style: "background: rgba(220, 53, 69, 0.04);",
					},
					h.h5(
						{ class: "fw-bold text-danger d-flex align-items-center gap-2 mb-2" },
						h.i({ class: "bi bi-exclamation-triangle" }),
						"Delete Account"
					),
					h.p(
						{ class: "text-muted small mb-3" },
						"Permanently delete your Carata account and personal data. We'll send a one-time code to confirm it's you. This can't be undone."
					),
					h.button(
						{
							type: "button",
							class: "btn btn-outline-danger rounded-pill px-4 py-2 fw-bold d-flex align-items-center gap-2",
							onclick: this.method("confirmDeleteAccount"),
						},
						h.i({ class: "bi bi-trash3" }),
						h.span({}, "Delete Account")
					)
				)
			)
		);
	}

	updateProfile() {
		let firstName = dom.id("profile-first-name");
		let lastName = dom.id("profile-last-name");
		let phone = dom.id("profile-phone");
		let displayName = dom.id("profile-display-name");
		let username = dom.id("profile-username");
		let bio = dom.id("profile-bio");
		let dob = dom.id("profile-dob");
		let gender = dom.id("profile-gender");

		if (!firstName?.value?.trim()) {
			return inform("First name cannot be empty.", "warning");
		}
		if (!lastName?.value?.trim()) {
			return inform("Last name cannot be empty.", "warning");
		}
		if (!phone?.value?.trim()) {
			return inform("Phone number cannot be empty.", "warning");
		}
		if (!displayName?.value?.trim()) {
			return inform("Display name cannot be empty.", "warning");
		}
		if (!username?.value?.trim()) {
			return inform("Username cannot be empty.", "warning");
		}
		if (this.usernameAvailable.value !== true) {
			return inform("Please choose an available username.", "warning");
		}
		if (!gender?.value) {
			return inform("Please select your gender.", "warning");
		}
		if (!dob?.value) {
			return inform("Please select your date of birth.", "warning");
		}

		let pd = {
			first_name: firstName.value,
			last_name: lastName.value,
			phone: phone.value,
			display_name: displayName.value,
			username: username.value,
			bio: bio.value,
			is_business: gc.user?.is_business ?? 0,
			dob: dob?.value,
			gender: gender?.value,
		};

		broker.broadcast($e.user.needs.update, payload(pd));
	}

	checkUsernameAvailability(target) {
		target.value = target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "");
		const username = target.value;
		this.usernameAvailable.value = null;

		if (username === gc.user?.username) {
			this.usernameAvailable.value = true;
			return;
		}

		if (!username || username.length < 3) return;
		this.usernameAvailable.value = "processing";

		if (this.debounceTimer) clearTimeout(this.debounceTimer);

		this.debounceTimer = setTimeout(async () => {
			try {
				const res = await api.get(`users/check-availability`, {
					username: username,
					exclude_id: gc.user?.id,
				});
				if (res) {
					this.usernameAvailable.value = res.data.available;
				}
			} catch (e) {
				console.error(e);
			}
		}, 500);
	}

	async confirmDeleteAccount() {
		processStart({ title: "Sending OTP" });
		let res = await api.post("/auth/send/otp");
		processEnd();

		if (!res) return;

		inform(res.message, STRINGS.SUCCESS);
		this.showDeleteAccountModal();
	}

	async resendDeleteOtp() {
		processStart({ title: "Sending OTP" });
		let res = await api.post("/auth/send/otp");
		processEnd();

		if (!res) return;

		inform(res.message, STRINGS.SUCCESS);
	}

	showDeleteAccountModal() {
		if (this.deleteAccountModal) {
			cleanDispose(this.deleteAccountModal);
		}

		let content = h.div(
			{ class: "modal-content border-0 shadow-lg rounded-4 overflow-hidden" },

			// Header
			h.div(
				{
					class: "modal-header border-0 pb-0 pt-4 px-4 d-flex justify-content-between align-items-center",
				},
				h.h5(
					{ class: "modal-title fw-bold text-danger d-flex align-items-center gap-2" },
					h.i({ class: "bi bi-exclamation-triangle-fill" }),
					"Delete Account"
				),
				h.button({
					class: "btn-close bg-light rounded-circle p-2",
					"data-bs-dismiss": "modal",
					aria_label: "Close",
				})
			),

			// Body
			h.div(
				{ class: "modal-body p-4" },
				h.p(
					{ class: "text-muted mb-4" },
					"This will schedule your account for permanent deletion in accordance with our data retention policy. Enter the One Time Password (OTP) sent to your phone and email to confirm."
				),
				h.div(
					{
						id: "delete-account-otp-input",
						class: "d-flex gap-2 justify-content-center mb-3",
					},
					h.call(() => {
						let divs = [];

						for (let i = 0; i < 5; i++) {
							divs.push(
								h.input({
									type: "number",
									class: "form-control fs-3 text-center",
									style: "width: 48px; padding: 8px 0;",
									hook: "delete-otp-input-" + i,
								})
							);
						}

						return divs;
					})
				),
				h.div(
					{ class: "text-center" },
					h.span(
						{
							class: "text-primary fw-500",
							role: "button",
							onclick: this.method("resendDeleteOtp"),
						},
						"Resend Code"
					)
				)
			),

			// Footer
			h.div(
				{ class: "modal-footer border-0 px-4 pb-4 pt-0 gap-2" },
				h.button(
					{
						class: "btn btn-light rounded-pill px-4 py-2 fw-bold flex-fill",
						"data-bs-dismiss": "modal",
					},
					"Cancel"
				),
				h.button(
					{
						id: "confirm-delete-account-btn",
						class: "btn btn-danger rounded-pill px-4 py-2 fw-bold flex-fill",
						onclick: this.method("submitAccountDeletion", ["${this}"]),
					},
					"Delete My Account"
				)
			)
		);

		this.deleteAccountModal = tool.plainModal(content, {
			id: "delete-account-modal",
			data_bs_backdrop: "static",
		});
		this.deleteAccountModal.show();

		setTimeout(() => {
			this.deleteAccountOtpInput = new VanillaOTP(dom.id("delete-account-otp-input"));
			dom.get("[hook=delete-otp-input-0]")?.focus();
		}, 150);
	}

	async submitAccountDeletion(btn) {
		let code = this.deleteAccountOtpInput?.getValue();

		if (!code || code.includes(" ")) {
			return inform("Please enter the full OTP code", STRINGS.WARNING);
		}

		dom.disable(btn);
		processStart({ title: "Deleting your account" });
		let res = await api.delete("user/account", { code });
		processEnd();
		dom.enable(btn);

		if (!res) return;

		this.deleteAccountModal?.hide();
		inform(res.message, STRINGS.SUCCESS);

		setTimeout(() => {
			broker.send($e.user.needs.logout);
		}, 1500);
	}

	cleanUp() {
		super.cleanUp();
		if (this.deleteAccountModal) cleanDispose(this.deleteAccountModal);
	}
}

ojs(EditProfile, FormField);
